import type {
  ExternalRegisterAlertStatus,
  ExternalRegisterAlertType,
  ExternalRegisterAuditEvent,
} from "@/components/externalregister/types";

export type ExternalRegisterLinkStatus = "linked" | "unlinked";
export type ExternalRegisterStatusFilter = ExternalRegisterAlertStatus | "all";
export type ExternalRegisterLinkStatusFilter = ExternalRegisterLinkStatus | "all";
export type ExternalRegisterAlertTypeFilter = ExternalRegisterAlertType | "all";

export type ExternalRegisterMaintenanceFilters = {
  status: ExternalRegisterStatusFilter;
  type: ExternalRegisterAlertTypeFilter;
  patientId: string;
  linkStatus: ExternalRegisterLinkStatusFilter;
};

export type ExternalRegisterMaintenancePagination = {
  limit: number;
  skip: number;
};

export type ExternalRegisterMaintenanceEntry = {
  id: string;
  status: ExternalRegisterAlertStatus;
  type: ExternalRegisterAlertType;
  hospital: string;
  dateListed: string;
  dateInactive?: string;
  surname: string;
  givenName: string;
  aliases: string[];
  sex?: string;
  dob?: string;
  umrn?: string;
  comments?: string;
  linkedPatientId?: string;
  previousLinkedPatientId?: string;
  linkStatus: ExternalRegisterLinkStatus;
  unlinkedAt?: string;
  unlinkedBy?: string;
  unlinkedReason?: string;
  addedBy?: string;
  modifiedBy?: string;
  created?: string;
  lastModified?: string;
  patientId?: string;
  patientName?: string;
  history?: Array<ExternalRegisterAuditEvent & { reason?: string }>;
};

export type ExternalRegisterAdminActionDialogState =
  | { kind: "inactivate"; entry: ExternalRegisterMaintenanceEntry }
  | { kind: "reactivate"; entry: ExternalRegisterMaintenanceEntry }
  | { kind: "unlink"; entry: ExternalRegisterMaintenanceEntry }
  | { kind: "relink"; entry: ExternalRegisterMaintenanceEntry }
  | null;

export type ExternalRegisterEntriesResponse = {
  total: number;
  data: ExternalRegisterMaintenanceEntry[];
};

export type ExternalRegisterEntryRecord = {
  type: ExternalRegisterAlertType;
  hospital: string;
  dateListed: string;
  surname: string;
  givenName: string;
  aliases: string[];
  sex?: string;
  dob?: string;
  umrn?: string;
  comments?: string;
};

export type ExternalRegisterCreateEntryRequest =
  | {
    patientId: string;
    entry: ExternalRegisterEntryRecord;
    createMinimalPatient?: false;
    createUnlinkedEntry?: false;
  }
  | {
    createMinimalPatient: true;
    entry: ExternalRegisterEntryRecord;
    patientId?: never;
    createUnlinkedEntry?: false;
  }
  | {
    createUnlinkedEntry: true;
    entry: ExternalRegisterEntryRecord;
    patientId?: never;
    createMinimalPatient?: false;
  };

export type ExternalRegisterDeleteNeverLinkedResponse = {
  id: string;
  deleted: true;
};

export type ExternalRegisterDevPurgeRequest = {
  confirmation: "PURGE_EXTERNAL_REGISTER";
  dryRun?: boolean;
};

export type ExternalRegisterDevPurgeResponse = {
  enabled: true;
  dryRun: boolean;
  purgedEntries: number;
  cleanedPatients: number;
  scannedPatients: number;
};

export type ExternalRegisterAlertTypeSettings = {
  label: string;
  color: string;
};

export type ExternalRegisterRetentionSettings = {
  enabled: false;
  mode?: "none" | "auto_inactivate" | "auto_purge";
  basis?: "dateListed" | "dateInactive" | "lastModified";
  years?: number;
  dryRun?: boolean;
  notifyBeforeDays?: number;
};

export type ExternalRegisterSettings = {
  conciergeIndicator: {
    enabled: boolean;
    tenantAllowed: boolean;
  };
  alertTypes: Record<ExternalRegisterAlertType, ExternalRegisterAlertTypeSettings>;
  retention: ExternalRegisterRetentionSettings;
};

export type ExternalRegisterSettingsPatch = {
  conciergeIndicator?: {
    enabled?: boolean;
    tenantAllowed?: boolean;
  };
  alertTypes?: Partial<Record<ExternalRegisterAlertType, {
    label?: string;
    color?: string;
  }>>;
  retention?: {
    enabled?: false;
  };
};

export type ExternalRegisterRetentionEvaluation = {
  enabled: false;
  evaluatedAt: string;
  wouldEvaluateCount: number;
  actionedCount: number;
};

export type ExternalRegisterAdminService = {
  listEntries: (
    filters: ExternalRegisterMaintenanceFilters,
    pagination?: ExternalRegisterMaintenancePagination,
  ) => Promise<ExternalRegisterEntriesResponse>;
  createEntry: (payload: ExternalRegisterCreateEntryRequest) => Promise<ExternalRegisterMaintenanceEntry>;
  updateEntry: (entryId: string, payload: Partial<ExternalRegisterEntryRecord>) => Promise<ExternalRegisterMaintenanceEntry>;
  inactivateEntry: (entryId: string, payload: { reason?: string }) => Promise<ExternalRegisterMaintenanceEntry>;
  reactivateEntry: (entryId: string, payload: { reason?: string }) => Promise<ExternalRegisterMaintenanceEntry>;
  unlinkEntry: (entryId: string, payload: { reason: string }) => Promise<ExternalRegisterMaintenanceEntry>;
  relinkEntry: (entryId: string, payload: { patientId: string; reason: string }) => Promise<ExternalRegisterMaintenanceEntry>;
  deleteNeverLinkedEntry: (entryId: string) => Promise<ExternalRegisterDeleteNeverLinkedResponse>;
  purgeExternalRegister: (payload: ExternalRegisterDevPurgeRequest) => Promise<ExternalRegisterDevPurgeResponse>;
  getSettings: () => Promise<ExternalRegisterSettings>;
  updateSettings: (patch: ExternalRegisterSettingsPatch) => Promise<ExternalRegisterSettings>;
  evaluateRetention: () => Promise<ExternalRegisterRetentionEvaluation>;
};

export const EXTERNAL_REGISTER_ADMIN_ALERT_TYPES: ExternalRegisterAlertType[] = [
  "SECURITY_PLAN",
  "MANAGEMENT_PLAN",
  "FAST_TRACK_MEDICAL",
  "CHILD_PROTECTION",
  "OTHER",
];

export const DEFAULT_EXTERNAL_REGISTER_ADMIN_FILTERS: ExternalRegisterMaintenanceFilters = {
  status: "all",
  type: "all",
  patientId: "",
  linkStatus: "all",
};
